/**
 * zod schemas for the primitive formats every row and patch is built from.
 *
 * Each is built from the one pattern that defines the format (`HLC_PATTERN`,
 * `ULID_PATTERN`, … — the same regex the validator `isValidHlc`, `isValidUlid`
 * tests), so there is exactly one definition of each, and `@norna/api-contract`
 * builds its wire schemas from these rather than re-declaring them.
 *
 * **Why `.regex`, not only `.refine`** (D37, the 2026-09-27 review §1.4): zod
 * cannot express a refine in JSON Schema, so a refine-only format was
 * `{"type": "string"}` in `openapi.json`, and the compatibility baseline would
 * have frozen "any string". The regex puts the `pattern` into the schema. Where
 * the pattern IS the validator (HLC, ULID, base64) it is the whole check; where
 * the validator checks more than a regex can (a field path's reserved segments,
 * an order key the library must accept) the refine stays behind the regex, which
 * aborts first so a malformed value still gets one issue. What validates is
 * unchanged (`primitives.test.ts` holds each schema against its validator).
 */
import { z } from 'zod';
import { BASE64_PATTERN } from './base64.js';
import { HLC_PATTERN } from './hlc.js';
import { FIELD_PATH_PATTERN, isValidFieldPath } from './lww.js';
import { isValidOrd, ORD_PATTERN } from './ordering.js';
import { ULID_PATTERN } from './ulid.js';

/** A JSON value: what a row field holds. */
export const jsonValueSchema = z.json();
export type JsonValue = z.infer<typeof jsonValueSchema>;

/** An HLC string, `<13-digit ms>-<5-digit counter>-<nodeId>`. */
export const hlcSchema = z.string().regex(HLC_PATTERN, { message: 'not an HLC "<ms>-<counter>-<nodeId>"' });

/** A dotted field path: `title`, `params.vs.clip`. */
export const fieldPathSchema = z
  .string()
  .regex(FIELD_PATH_PATTERN, { message: 'not a dotted field path', abort: true })
  .refine(isValidFieldPath, { message: 'not a dotted field path' });

/** A canonical ULID: row ids, rundown ids, the `changedAt` cursor. */
export const ulidSchema = z.string().regex(ULID_PATTERN, { message: 'not a ULID' });

/** A sibling-order key (`ordering.ts`). */
export const ordSchema = z
  .string()
  .regex(ORD_PATTERN, { message: 'not a fractional-index order key', abort: true })
  .refine(isValidOrd, { message: 'not a fractional-index order key' });

/** Padded base64: an embedded Yjs state or update. */
export const base64Schema = z.string().regex(BASE64_PATTERN, { message: 'not padded base64' });

/**
 * An opaque writer / operation id (`origin`, `clientOpId`): bounded, and no
 * whitespace, because both are logged and used as keys.
 */
export const opaqueIdSchema = z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/, { message: 'not an id [A-Za-z0-9._:-]{1,128}' });

/**
 * A duration in milliseconds. Capped at 24 h: no part, piece or transition in a
 * live production is longer, and a cap keeps a typo (a value in microseconds)
 * from being accepted as a 30-year duration.
 */
export const MAX_DURATION_MS = 24 * 60 * 60 * 1000;
export const durationMsSchema = z.number().int().nonnegative().max(MAX_DURATION_MS);

/** Milliseconds since the Unix epoch. */
export const epochMsSchema = z.number().int().nonnegative();
